<template>
  <fragment>
    <list-skeleton v-if="loading && !hasNextPage" list-type="student" />
    <ul v-else class="dashboard-list color-coded">
      <li class="row header-labels sticky">
        <div class="col-6 subject">
          <div>User</div>
        </div>
        <div class="col-6 data">
          <transition name="update" mode="out-in">
            <div :key="creditDisplayType">Allowance</div>
          </transition>
          <transition name="update" mode="out-in">
            <div :key="creditDisplayType">Used</div>
          </transition>
          <transition name="update" mode="out-in">
            <div :key="creditDisplayType">Remaining</div>
          </transition>
        </div>
      </li>
      <li v-if="!students.length" class="empty">No Results</li>
      <li
        v-for="student in students"
        :key="student.id"
        class="row"
        :class="`student-${student.isActive ? 'enabled' : 'disabled'}`"
        role="button"
        tabindex="0"
        aria-label="Edit Credit Allowance"
        @click="openModal(student)"
      >
        <div class="col-12 col-sm-6 subject">
          <div class="avatar hide-sm-down">
            <div class="image">
              <div
                v-if="student.photoUrl"
                class="user-img"
                :style="`background-image: url(${student.photoUrl});`"
              ></div>
            </div>
          </div>
          <div>
            <div class="primary">
              {{ student.fullName }}
            </div>
            <div class="secondary">
              <div>{{ student.email }}</div>
              <div :class="{ warn: !student.lastLogin }">
                {{
                  student.lastLogin
                    ? ` last logged in ${formatLastLoggedInAsDate(student.lastLogin)}`
                    : 'has never logged in'
                }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-6 data">
          <transition name="update" mode="out-in">
            <div
              :key="
                calculateKey(student, `${creditDisplayType}CreditAllowance`)
              "
            >
              <em class="sr-only-sm-up">Allowance</em>
              {{
                student[`${creditDisplayType}CreditAllowance`].toLocaleString()
              }}
            </div>
          </transition>
          <transition name="update" mode="out-in">
            <div
              :key="calculateKey(student, `${creditDisplayType}CreditsUsed`)"
            >
              <em class="sr-only-sm-up">Used</em>
              {{ student[`${creditDisplayType}CreditsUsed`].toLocaleString() }}
            </div>
          </transition>
          <transition name="update" mode="out-in">
            <div
              :key="
                calculateKey(student, `${creditDisplayType}CreditsRemaining`)
              "
            >
              <em class="sr-only-sm-up">Rem.</em>
              <span class="credits">
                {{
                  student[
                    `${creditDisplayType}CreditsRemaining`
                  ].toLocaleString()
                }}

                <img src="@/assets/icon/icon-credit.svg" alt="Credit" />
              </span>
            </div>
          </transition>
        </div>
      </li>
    </ul>

    <div v-if="!mobileList && hasNext" class="list-progress-bar">
      <div class="value"></div>
      <span class="sr-only">loading</span>
    </div>

    <div v-if="mobileList && hasNext" class="load-more">
      <button class="dash-button" @click="handleLoadMore">
        Load More Results
      </button>
    </div>

    <div v-if="students.length > 20 && !hasNext" class="end-of-list">
      <font-awesome-icon :icon="['fas', 'check-circle']" class="icon alt" />
      <p>All Results Loaded</p>
    </div>
  </fragment>
</template>

<script>
import moment from 'moment';
import EditStudentCreditsModal from '../../Credits/EditStudentCreditsModal.vue';
import ListSkeleton from '../../ListSkeleton.vue';

export default {
  name: 'StudentsList',

  components: {
    ListSkeleton
  },

  inject: ['studentDetailsReport'],

  props: {
    hasNextPage: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: () => true
    }
  },

  data() {
    return {
      windowWidth: window.innerWidth
    };
  },

  computed: {
    creditDisplayType() {
      return this.studentDetailsReport.creditDisplayType;
    },
    hasNext() {
      return this.studentDetailsReport.hasNext;
    },
    students() {
      return this.studentDetailsReport.data.studentDetails;
    },
    mobileList() {
      if (this.windowWidth < 990) {
        return true;
      }
      return false;
    }
  },

  created() {
    window.addEventListener('scroll', this.handleScroll, { once:true });
    window.addEventListener('resize', this.onResize);
  },

  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    calculateKey(student, propName) {
      return student.id + this.creditDisplayType + student[propName];
    },

    openModal(student) {
      this.$modal.show(EditStudentCreditsModal, {
        props: {
          schoolId: this.studentDetailsReport.schoolId,
          student: student
        }
      });
    },

    async handleScroll() {
      const atBottomOfWindow =
        document.documentElement.scrollTop + window.innerHeight >=
        document.documentElement.offsetHeight * 0.8;
      if (!this.mobileList && atBottomOfWindow && this.hasNext) {
        await this.studentDetailsReport.fetchNext();
      }
      window.addEventListener('scroll', this.handleScroll, { once:true });
    },

    handleLoadMore() {
      this.studentDetailsReport.fetchNext();
    },

    formatLastLoggedInAsTimespan(datetime) {
      return moment(datetime).fromNow();
    },
    formatLastLoggedInAsDate(datetime) {
      return moment(datetime).format('M/D/YYYY h:mmA');
    },

    onResize() {
      this.windowWidth = window.innerWidth;
    }
  }
};
</script>
